import { Fragment, FunctionComponent, ReactNode } from "react";

import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";

import { WhitelabelSettingReadModel } from "../../../../../../../@api";

import { ISettingListItem } from "../../Models/interfaces/setting-list-item.interface";
import { SettingItem } from "../SettingItem/SettingItem";

import "./SettingList.scss";

export interface ISettingListProps<T extends ISettingListItem> {
  list: T[];
  whiteLabelSettings: WhitelabelSettingReadModel[];
  resetSetting: (setting: T, wlSetting: WhitelabelSettingReadModel) => void;
}

export const SettingList: FunctionComponent<ISettingListProps<any>> = (props: ISettingListProps<any>) => {
  const findWhitelabelSetting = (setting: ISettingListItem): WhitelabelSettingReadModel | undefined => {
    return props.whiteLabelSettings.find(wlSetting => wlSetting.key === setting.settingKey);
  };

  if (!props.whiteLabelSettings?.length) {
    return <ProgressSpinner className="loading-setting-spinner" />;
  }

  const isEmptyOrNull = (value: any): boolean => {
    return value === null || value === undefined || value === "";
  };

  const resetToDefaultTemplate = (setting: ISettingListItem): ReactNode => {
    const wlSetting = findWhitelabelSetting(setting);

    if (
      !setting.hasReset ||
      !wlSetting ||
      isEmptyOrNull(wlSetting.customValue) ||
      wlSetting.customValue === wlSetting.defaultValue
    ) {
      return;
    }

    return (
      <Button
        onClick={() => props.resetSetting(setting, wlSetting)}
        onKeyDown={e => e.key === "Enter" && props.resetSetting(setting, wlSetting)}
        tabIndex={0}
        aria-label="Reset"
        className="unstyled-button setting-reset-button"
      >
        Reset
      </Button>
    );
  };

  const isDefault = (setting: ISettingListItem): boolean => {
    const wlSetting = findWhitelabelSetting(setting);
    if (!wlSetting) {
      return false;
    }

    return isEmptyOrNull(wlSetting.customValue) || wlSetting.customValue === wlSetting.defaultValue;
  };

  return (
    <div className="setting-list">
      {props.list.map((setting: ISettingListItem) => (
        <Fragment key={setting.settingKey}>
          <SettingItem
            titleKey={setting.labelKey}
            centerVertically={setting.centerVertically}
            resetTemplate={resetToDefaultTemplate(setting)}
            isDefault={isDefault(setting)}
            exampleContent={setting.exampleContent}
            valueFunction={() =>
              findWhitelabelSetting(setting)?.customValue ?? findWhitelabelSetting(setting)?.defaultValue
            }
          >
            {setting.itemTemplate(setting, findWhitelabelSetting(setting))}
          </SettingItem>
        </Fragment>
      ))}
    </div>
  );
};
